import { useShellSession } from '../../components/shell/useShellSession';

export interface UseCapability {
  /** True when the viewer's membership row for `orgId` carries `key` in
   *  `permission_groups.capability_keys`. False when `orgId` is null, when the
   *  user has no row in that org, when the row's permission group does not
   *  grant the capability, or while the session is still loading. */
  allowed: boolean;
  /** True while `useShellSession` is still resolving (`status === 'loading'`).
   *  Consumers can wait for `!loading` before deciding to redirect / deny. */
  loading: boolean;
}

/**
 * Capability check sourced from the already-loaded `useShellSession()`
 * memberships array. No extra round-trip — `useShellSession`'s `loadForUser`
 * joins `permission_groups.capability_keys` on the membership row, so this
 * hook is a pure derivation.
 *
 * For v1 the only consumer is the billing surface (`billing.manage`); existing
 * ad-hoc capability derivations in other VMs (e.g. `useMembersVM` lines
 * 499-510 deriving from `system_key`) are NOT being refactored in this
 * cluster — that's deferred until the permission-group customization feature
 * lands.
 *
 * @param orgId — active org id. `null` returns `{ allowed: false }` so callers
 *   can use the hook unconditionally while their VM's `activeOrgId` resolves.
 * @param key — capability key from `public.capabilities` (e.g. `'billing.manage'`).
 */
export function useCapability(orgId: string | null, key: string): UseCapability {
  const session = useShellSession();
  const loading = session.status === 'loading';
  if (!orgId) {
    return { allowed: false, loading };
  }
  const row = session.memberships.find((m) => m.org_id === orgId);
  if (!row) {
    return { allowed: false, loading };
  }
  return { allowed: row.capability_keys.includes(key), loading };
}
